import { useEffect, useMemo, useCallback, useState, useContext } from 'react';

import { useQuery, useBooleanState } from '@vyce/core/src/hooks';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { CardForm, INVITE_WORKERS_PERMISSIONS, Subscription } from '@vyce/core/src/types';
import { createSubscriptionRequest } from '@vyce/core/src/api/billing';

import { useActions, useTypedSelector } from '../hooks';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

export const useInviteButton = () => {
  const [isActivateSubscriptionDialogOpen, openActivateSubscriptionDialog, closeActivateSubscriptionDialog] =
    useBooleanState(false);
  const { handleServerError } = useContext(NotificationContext);
  const [isInviteFormOpen, setOpenInviteForm, setCloseInviteForm] = useBooleanState(false);
  const [subscribeLoading, setSubscribeLoading] = useState<boolean>(false);
  const { updateSelectedCompanySubscription } = useActions();
  const query = useQuery();
  const { canAny } = usePermissions();
  const { selectedCompanyAppData, selectedCompany, access_token } = useTypedSelector(state => state.helper);

  // The single choke point for every invite entry point in the app: subscription
  // decides whether the form can open at all, this decides who may see the button.
  // Either invite right will do; the backend 403s the dialog and its pickers otherwise.
  const canInvite = canAny(INVITE_WORKERS_PERMISSIONS);

  const { paySchedules = [], subscription } = selectedCompanyAppData ?? {};
  const selectedCompanyIsSubscribed = useMemo(() => subscription?.active ?? false, [subscription]);

  const onlyOutsourcedPaySchedules = useMemo(
    () => paySchedules.every(item => item.outsourced),
    [paySchedules]
  );

  const inviteFormCanBeOpen = selectedCompanyIsSubscribed || onlyOutsourcedPaySchedules;

  // Default subscribe function with common logic
  // if you have another logic you can not use it
  const simpleSubscribe = useCallback(
    async (data: CardForm) => {
      if (!selectedCompany?.uuid) {
        return;
      }
      setSubscribeLoading(true);
      try {
        const res = await createSubscriptionRequest(access_token, selectedCompany.uuid, data);
        const subscription: Subscription = res.data;
        setSubscribeLoading(false);
        if (updateSelectedCompanySubscription) {
          updateSelectedCompanySubscription(subscription);
        }
        closeActivateSubscriptionDialog();
        setOpenInviteForm();
      } catch (e) {
        handleServerError(e);
        setSubscribeLoading(false);
      }
    },
    [selectedCompany, access_token, updateSelectedCompanySubscription]
  );

  const handleInviteButtonClick = () => {
    if (inviteFormCanBeOpen) {
      setOpenInviteForm();
      return;
    }
    openActivateSubscriptionDialog();
  };

  // Also when ?invite=1 is pushed onto the page already open, as a Laila card does.
  const inviteDialog = !!query.get('invite');
  useEffect(() => {
    // ?invite=1 is a deep link — it must not open the form for someone who
    // isn't allowed to invite.
    if (inviteDialog && canInvite) {
      setOpenInviteForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canInvite, inviteDialog]);

  return {
    canInvite,
    isActivateSubscriptionDialogOpen,
    inviteFormCanBeOpen,
    isInviteFormOpen,
    subscribeLoading,
    handleInviteButtonClick,
    setCloseInviteForm,
    simpleSubscribe,
    openActivateSubscriptionDialog,
    closeActivateSubscriptionDialog,
  };
};
